import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Link,
  Switch,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer,
  IconButton,
} from "@mui/material";
import {
  GetMemberDetailsResponse,
  NotificationSettingsValue,
} from "protogen/member_service_pb";
import FlexPage from "components/layout/FlexPage";
import AuthService from "services/auth";
import Breadcrumbs from "components/common/Breadcrumbs";
import {
  useGetMemberDetails,
  useGetBillingPortalUrl,
  useUpdateMemberNotifications,
  useUpdateMembersFamily,
  useUpdateMemberProfile,
} from "services/member";
import Loading from "components/common/Loading";
import { ArrowRight } from "lucide-react";
import { HomeIcon } from "@heroicons/react/24/outline";
import { isStandaloneWebapp } from "../common/utils";
import { getMembershipHours } from "components/pipeline/utils";
import TimeUtilization from "components/time-utilization/TimeUtilization";
import useIsMobile from "components/hooks/useIsMobile";
import PhoneInput from "components/creation/PhoneInput";
import { cleanPhone, formatUSPhoneNumber } from "common/utils";
import { validateContact } from "components/member/utils";
import { EphemeralMember, FormErrors } from "components/member/types";
import { Member } from "protogen/common_pb";
import FlexBox from "components/common/FlexBox";
import Address from "components/family/Address";
import { ReactComponent as CloseIcon } from "../icons/Close.svg";
import CurrentUserAvatar from "components/common/CurrentUserAvatar";
import { AvatarBadgeType } from "components/common/CurrentUserAvatar";
import { useUploader } from "components/creation/FileUploader";
import { downsizeImage } from "common/imageUtils";
import { refreshToken } from "services/api";
import { FamilyStatus } from "protogen/advisors_service_pb";

const formatBillingAmount = (amountCents: number | undefined): string => {
  if (amountCents === undefined) return "??/mo";
  return `$${(amountCents / 100).toString()}/mo`;
};

const AddressView = ({ address }: { address: string }) => {
  try {
    const parsed = JSON.parse(address);
    return (
      <Box>
        <Box>{parsed.formattedAddress}</Box>
      </Box>
    );
  } catch (error) {
    return <></>;
  }
};

const AddressSection = ({
  address,
  handleClick,
}: {
  address: string;
  handleClick: (address: string) => void;
}) => {
  const hasAddress = !!address;
  return (
    <Box sx={{ marginTop: "24px" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginBottom: "20px" }}
      >
        <Box display="flex" alignItems="center">
          <HomeIcon
            height="24px"
            width="24px"
            stroke="#6B6E7B"
            strokeWidth="2px"
          />
          <Typography
            variant="h3"
            sx={{ fontSize: "20px", fontWeight: 700, marginLeft: "8px" }}
          >
            Home address
          </Typography>
        </Box>
        {hasAddress && (
          <Typography
            onClick={() => {
              handleClick(address);
            }}
            variant="bodySmall"
            sx={{
              color: "#6B6E7B",
              cursor: "pointer",
            }}
          >
            edit
          </Typography>
        )}
      </Box>
      {hasAddress && (
        <Box
          sx={{
            border: "1px solid #D4D4D4",
            borderRadius: "8px",
            display: "flex",
            padding: "20px",
            flexDirection: "column",
            fontWeight: "500",
            gap: "20px",
          }}
        >
          <AddressView address={address || ""} />
        </Box>
      )}
      {!hasAddress && (
        <Button
          variant="outlined"
          onClick={() => {
            handleClick("");
          }}
        >
          Add address
        </Button>
      )}
    </Box>
  );
};

const AddPartnerForm = ({
  member,
  onChange,
  errors,
}: {
  member: EphemeralMember;
  onChange: (member: EphemeralMember) => void;
  errors?: FormErrors;
}) => {
  const isMobile = useIsMobile();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <TextField
          sx={{
            width: isMobile ? "100%" : undefined,
            flex: !isMobile ? 1 : undefined,
          }}
          margin="dense"
          label="First name"
          type="text"
          variant="outlined"
          value={member.firstName}
          onChange={(e) => {
            onChange({
              ...member,
              firstName: e.target.value,
            });
          }}
        />
        <TextField
          sx={{
            width: isMobile ? "100%" : undefined,
            flex: !isMobile ? 1 : undefined,
          }}
          margin="dense"
          label="Last name"
          type="text"
          variant="outlined"
          value={member.lastName}
          onChange={(e) => {
            onChange({
              ...member,
              lastName: e.target.value,
            });
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <PhoneInput
          value={member.primaryPhone}
          error={!!errors?.primaryPhone}
          helperText={errors?.primaryPhone}
          onChange={(phone) => {
            onChange({
              ...member,
              primaryPhone: cleanPhone(
                typeof phone == "string" ? phone : phone.target.value,
              ),
            });
          }}
          label="Phone"
          size="small"
          sx={{
            width: isMobile ? "100%" : undefined,
            flex: !isMobile ? 1 : undefined,
            marginTop: "8px",
            marginBottom: "4px",
          }}
          autoComplete="off"
        />
        <TextField
          sx={{
            width: isMobile ? "100%" : undefined,
            flex: !isMobile ? 1 : undefined,
          }}
          margin="dense"
          label="Email"
          name="username"
          type="email"
          variant="outlined"
          value={member.primaryEmail}
          error={!!errors?.primaryEmail}
          helperText={errors?.primaryEmail}
          onChange={(e) => {
            onChange({
              ...member,
              primaryEmail: e.target.value,
            });
          }}
        />
      </Box>
    </Box>
  );
};

const MemberSection = ({ member }: { member: Member }) => (
  <FlexBox gap="2px" direction="column">
    <Typography variant="bodyHeavy">
      {member.firstName} {member.lastName}
    </Typography>
    <Typography variant="body">
      {formatUSPhoneNumber(member.primaryPhone)}
    </Typography>
    <Typography variant="body">{member.primaryEmail}</Typography>
  </FlexBox>
);

const ProfileSection = ({
  memberDetails,
  onProfileUpdate,
}: {
  memberDetails: GetMemberDetailsResponse | null;
  disabled: boolean;
  onProfileUpdate: () => void;
}) => {
  const isMobile = useIsMobile();

  const { request: updateProfileRequest } = useUpdateMemberProfile(() => {
    onProfileUpdate();
  });

  const { onUpload } = useUploader({
    maxUploadBytes: 4900000,
  });

  const handleProfileImageUpload = async (files: FileList | null) => {
    if (!files || !memberDetails?.member) return;

    const downsizedFile = await downsizeImage(files[0], 256, 256);
    onUpload([downsizedFile.file], undefined, async (attachments) => {
      const attachment = attachments.pop();
      if (attachment) {
        await updateProfileRequest({
          profileImage: attachment,
          shouldUpdateProfileImage: true,
        });
        refreshToken();
      }
    });
  };

  return (
    <Box sx={{ marginTop: "24px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "16px",
        }}
      >
        <Typography variant="h3">Profile</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "32px",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            aspectRatio: "1/1",
            flexGrow: 1,
            maxWidth: isMobile ? "100px" : "158px",
            width: "100%",
          }}
        >
          <CurrentUserAvatar
            user={memberDetails?.member}
            size={158}
            autoscale={true}
            badgeType={AvatarBadgeType.FILE_UPLOAD}
            onUpload={handleProfileImageUpload}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">
            {memberDetails?.member?.firstName} {memberDetails?.member?.lastName}
          </Typography>
          <Typography variant="body" color="text.secondary">
            Click the edit icon to upload a profile picture
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const AccountHolders = ({
  memberDetails,
  disabled,
  onNewMember,
  shouldOpenAddDialog,
  onAddDialogStateChange,
}: {
  memberDetails: GetMemberDetailsResponse | null;
  disabled: boolean;
  onNewMember: () => void;
  shouldOpenAddDialog?: boolean;
  onAddDialogStateChange?: (isOpen: boolean) => void;
}) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newMember, setNewMember] = useState<EphemeralMember>({
    firstName: "",
    lastName: "",
    primaryPhone: "",
    primaryEmail: "",
  });
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  const { request: updateFamilyRequest, loading: updateMembersFamilyLoading } =
    useUpdateMembersFamily();

  // Create disallowed contacts list from existing family members
  const disallowedContacts = useMemo(() => {
    const existingMembers: Member[] =
      memberDetails?.family?.familyMembers || [];
    return {
      emails: existingMembers
        .filter((m) => m.primaryEmail)
        .map((m) => m.primaryEmail),
      phones: existingMembers
        .filter((m) => m.primaryPhone)
        .map((m) => m.primaryPhone),
    };
  }, [memberDetails?.family?.familyMembers]);

  const validateNewMember = () => {
    const errors = validateContact(newMember);

    // Check for duplicate email
    if (
      newMember.primaryEmail &&
      disallowedContacts?.emails.includes(newMember.primaryEmail)
    ) {
      errors.primaryEmail =
        "This email is already in use by another family member";
    }

    // Check for duplicate phone
    if (
      newMember.primaryPhone &&
      disallowedContacts?.phones.includes(newMember.primaryPhone)
    ) {
      errors.primaryPhone =
        "This phone number is already in use by another family member";
    }

    return errors;
  };

  // Handle external request to open dialog (from query param)
  useEffect(() => {
    if (shouldOpenAddDialog && !isAddDialogOpen) {
      setValidationErrors({});
      setIsAddDialogOpen(true);
      onAddDialogStateChange?.(true);
    }
  }, [shouldOpenAddDialog, isAddDialogOpen, onAddDialogStateChange]);

  const handleSave = async () => {
    const errors = validateNewMember();
    setValidationErrors(errors);

    // Check if there are any validation errors
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (memberDetails?.family?.ref) {
      await updateFamilyRequest({
        familyRef: memberDetails.family.ref,
        updatedAddress: memberDetails.family.address,
        updatedMembers: [],
        newMembers: [
          {
            firstName: newMember.firstName || "",
            lastName: newMember.lastName || "",
            primaryPhone: newMember.primaryPhone || "",
            primaryEmail: newMember.primaryEmail || "",
          },
        ],
      });

      // Reset form and close dialog
      setNewMember({
        firstName: "",
        lastName: "",
        primaryPhone: "",
        primaryEmail: "",
      });
      setValidationErrors({});
      setIsAddDialogOpen(false);
      onAddDialogStateChange?.(false);
      onNewMember();
    }
  };

  const handleCancel = () => {
    setNewMember({
      firstName: "",
      lastName: "",
      primaryPhone: "",
      primaryEmail: "",
    });
    setValidationErrors({});
    setIsAddDialogOpen(false);
    onAddDialogStateChange?.(false);
  };

  const familyMembers = memberDetails?.family?.familyMembers || [];
  const showAddButton = familyMembers.length === 1;

  return (
    <Box sx={{ marginTop: "24px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "16px",
        }}
      >
        <Typography variant="h3">Household Accounts</Typography>
        {showAddButton && (
          <Button
            onClick={() => {
              setValidationErrors({});
              setIsAddDialogOpen(true);
            }}
            disabled={disabled}
            size="small"
            variant="text"
          >
            Add a partner
          </Button>
        )}
      </Box>

      <FlexBox direction="column" gap="8px">
        {familyMembers.map((member: Member, index: number) => (
          <MemberSection key={member.ref || index} member={member} />
        ))}
      </FlexBox>

      <Dialog
        open={isAddDialogOpen}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add a partner</DialogTitle>
        <DialogContent>
          <Box sx={{ paddingTop: "16px" }}>
            <AddPartnerForm
              member={newMember}
              errors={validationErrors}
              onChange={(updatedMember: EphemeralMember) => {
                setNewMember(updatedMember);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMembersFamilyLoading}
            variant="contained"
          >
            Add partner
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Notifications = ({
  memberDetails,
  disabled,
}: {
  memberDetails: GetMemberDetailsResponse | null;
  disabled: boolean;
}) => {
  const { request, loading } = useUpdateMemberNotifications();
  const _disabled = disabled || loading;
  const onChangeEmail = async (val: boolean) => {
    if (_disabled) return;
    await request({
      settings: {
        email: val
          ? NotificationSettingsValue.ENABLED
          : NotificationSettingsValue.DISABLED,
        push: NotificationSettingsValue.UNSPECIFIED,
      },
    });
  };
  const onChangePush = async (val: boolean) => {
    if (_disabled) return;
    await request({
      settings: {
        push: val
          ? NotificationSettingsValue.ENABLED
          : NotificationSettingsValue.DISABLED,
        email: NotificationSettingsValue.UNSPECIFIED,
      },
    });
  };
  return (
    <Box sx={{ marginTop: "24px" }}>
      <Box>
        <Typography variant="h3" sx={{ paddingBottom: "16px" }}>
          Notifications
        </Typography>
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body">Receive updates by email</Typography>
            <Switch
              disabled={
                _disabled ||
                memberDetails?.notificationSettings?.email ===
                  NotificationSettingsValue.UNSPECIFIED
              }
              defaultChecked={
                memberDetails?.notificationSettings?.email ===
                NotificationSettingsValue.ENABLED
              }
              onChange={(e) => onChangeEmail(e.target.checked)}
            />
          </Box>
          {isStandaloneWebapp() && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body">
                Receive push notifications for updates
              </Typography>
              <Switch
                disabled={
                  _disabled ||
                  memberDetails?.notificationSettings?.push ===
                    NotificationSettingsValue.UNSPECIFIED
                }
                defaultChecked={
                  memberDetails?.notificationSettings?.push ===
                  NotificationSettingsValue.ENABLED
                }
                onChange={(e) => onChangePush(e.target.checked)}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CalendarSettings = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginBottom: "16px",
    }}
  >
    <Typography variant="h3" sx={{ paddingBottom: "16px" }}>
      Calendar settings
    </Typography>
    <Link
      href="/settings/calendars"
      style={{
        textDecoration: "none",
        color: "#198282",
        fontWeight: "600",
        cursor: "pointer",
      }}
    >
      Manage your calendar connections
    </Link>
  </Box>
);

const PlanDetails = ({
  memberDetails,
  loading,
}: {
  memberDetails: GetMemberDetailsResponse | null;
  loading: boolean;
}) => {
  const isActive = memberDetails?.family?.status === FamilyStatus.ACTIVE;

  const { request: billingUrlRequest } = useGetBillingPortalUrl();
  const onBillingRedirect = async () => {
    // Open a blank tab immediately upon user interaction, and then change that tab’s
    // location once we have the URL. This maintains the synchronous nature of the initial action:
    const newWindow = window.open("", "_blank");
    const result = await billingUrlRequest();
    const url = result?.url;
    if (newWindow && url) {
      newWindow.location.href = url;
    } else if (newWindow) {
      // If no URL is available, close the tab or show some error message
      newWindow.close();
    }
  };
  const isMobile = useIsMobile();
  const renewalDate = new Date(
    memberDetails?.planUtilization?.periodEndDate || "",
  );
  renewalDate.setDate(renewalDate.getDate() + 1);

  return (
    <Box sx={{ width: "100%" }}>
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box>
            <Typography variant="h3">Plan details</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              border: "1px solid #D4D4D4",
              borderRadius: "12px",
              gap: isMobile ? "0" : "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: "1",
                gap: "4px",
                padding: "16px 20px",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h4" color="#3D3D3D">
                  {memberDetails?.family?.billingInfo?.product?.name}
                </Typography>
                <Typography variant="bodyHeavy" color="#3D3D3D">
                  {formatBillingAmount(
                    memberDetails?.family?.billingInfo?.product?.amountCents,
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                {isActive ? (
                  <Typography variant="body" color="#3D3D3D">
                    Renews{" "}
                    {renewalDate.toLocaleDateString("en-US", {
                      timeZone: "UTC",
                      day: "numeric",
                      month: "long",
                    })}
                  </Typography>
                ) : (
                  <Typography variant="body" color="#3D3D3D">
                    Service not started
                  </Typography>
                )}
                <Typography variant="body" color="#3D3D3D" textAlign="right">
                  {memberDetails?.family?.billingInfo?.product?.name &&
                    getMembershipHours(
                      memberDetails?.family?.billingInfo?.product?.name,
                    )}{" "}
                  hours
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
              ></Box>
            </Box>

            {isActive && (
              <Divider
                flexItem
                orientation={isMobile ? "horizontal" : "vertical"}
              />
            )}
            {isActive && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flex: "1",
                  padding: "16px 20px",
                }}
              >
                <TimeUtilization
                  planUtilization={memberDetails?.planUtilization}
                  showRenewal={false}
                  serviceEnding={!!memberDetails?.family?.endDate}
                />
              </Box>
            )}
          </Box>

          <Box sx={{}}>
            <Link
              sx={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                padding: "18px 20px 16px",
                borderRadius: "12px",
                border: "1px solid #D4D4D4",
              }}
              onClick={(e) => {
                e.preventDefault();
                onBillingRedirect();
              }}
            >
              <Typography variant="bodyHeavy">
                Update billing information
              </Typography>
              <ArrowRight width="20" color="#6B6E7B" />
            </Link>
          </Box>

          <Box>
            <Typography variant="body">
              If you'd like to update your subscription, contact your Advisor
              directly. For any platform questions or concerns, contact{" "}
              <Link href="mailto:hi@findfaye.com">Faye support</Link>.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { request, data, loading, error } = useGetMemberDetails();
  const isMobile = useIsMobile();
  const [shouldOpenAddDialog, setShouldOpenAddDialog] = useState(false);
  const [showAddressPanel, setShowAddressPanel] = useState(false);

  const handleClickAddress = (address: string) => {
    if (isMobile) {
      navigate("/family-info/address", {
        state: { familyAddress: address, familyRef: data?.family?.ref },
      });
    } else {
      setShowAddressPanel(true);
    }
  };

  const closeAddressPanel = (doRefresh?: boolean) => {
    setShowAddressPanel(false);
    if (doRefresh) {
      request();
    }
  };

  const onSaveAddressHandler = () => closeAddressPanel(true);
  const onCancelAddressHandler = () => closeAddressPanel();

  const handleLogout = async () => {
    await AuthService.logout();
    // Clear local storage (redundant on some auth keys).
    localStorage.clear();
    navigate("/");
  };

  const onNewMember = () => {
    request();
  };

  const handleAddDialogStateChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Remove the query parameter when dialog closes
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("addMember");
      setSearchParams(newSearchParams, { replace: true });
      setShouldOpenAddDialog(false);
    }
  };

  useEffect(() => {
    request();
  }, []);

  // Check for addMember query parameter
  useEffect(() => {
    if (searchParams.get("addMember") === "true") {
      setShouldOpenAddDialog(true);
      // Remove the query parameter immediately to clean up the URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("addMember");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <FlexPage
      leftAligned
      fullHeight
      headerPadding={false}
      sx={{
        justifyContent: "space-between",
        maxWidth: "700px",
        overflowY: "auto",
      }}
    >
      <Box>
        <Breadcrumbs
          breadcrumbs={[{ name: "Home", link: "/" }, { name: "Settings" }]}
        />
        {data ? (
          <Box display={"flex"} flexDirection={"column"} gap={"24px"}>
            <Box sx={{}}>
              <Typography variant="display">Settings</Typography>
            </Box>
            <ProfileSection
              memberDetails={data}
              disabled={loading}
              onProfileUpdate={() => request()}
            />
            <PlanDetails memberDetails={data} loading={loading || !!error} />
            <AddressSection
              address={data?.family?.address || ""}
              handleClick={handleClickAddress}
            />
            <AccountHolders
              memberDetails={data}
              disabled={loading}
              onNewMember={onNewMember}
              shouldOpenAddDialog={shouldOpenAddDialog}
              onAddDialogStateChange={handleAddDialogStateChange}
            />
            <Notifications memberDetails={data} disabled={loading} />
            <CalendarSettings />
          </Box>
        ) : (
          <Loading />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "6px",
          alignItems: "center",
          marginTop: isMobile ? "24px" : "0",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleLogout}
          sx={{ alignSelf: "flex-start" }}
        >
          Log out
        </Button>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "end",
          }}
        >
          <Link
            href="https://www.findfaye.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Link>
          <Link
            href="https://www.findfaye.com/client-terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Client Terms of Service
          </Link>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={showAddressPanel}
        onClose={onCancelAddressHandler}
        sx={{
          "& .MuiDrawer-paper": {
            boxShadow: "-2px 0px 4px 0px rgba(0, 0, 0, 0.15)",
            width: isMobile ? "100%" : "600px",
            boxSizing: "border-box",
          },
        }}
      >
        <Box>
          <Box
            sx={{
              marginTop: "32px",
              marginLeft: "24px",
              marginRight: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
            }}
          >
            <Breadcrumbs breadcrumbs={[{ name: "Address" }]} />
            <IconButton
              onClick={onCancelAddressHandler}
              sx={{
                height: "36px",
                width: "36px",
                padding: 0,
                border: "2px solid #EDEDED",
                borderRadius: "100px",
              }}
            >
              <CloseIcon height="12px" width="12px" stroke="#8E9598" />
            </IconButton>
          </Box>
          <Address
            familyRef={data?.family?.ref || ""}
            address={data?.family?.address || ""}
            onSaveHandler={onSaveAddressHandler}
            onCancelHandler={onCancelAddressHandler}
          />
        </Box>
      </Drawer>
    </FlexPage>
  );
};
